import { useCallback, useEffect, useRef, useState } from 'react';

import {
    getCookieJsonValue,
    getCookieValue,
    setCookieJsonValue,
    setCookieValue,
} from 'utility/Track/cookies';

export const useCookie = (
    cookieName: string,
    defaultValue: string,
    defaultLifeTime = 0
): [string, (value: string, lifeTime?: number) => void] => {
    const getCookie = () => getCookieValue(cookieName) || defaultValue;

    const initializedRef = useRef(false);
    const lifeTimeRef = useRef(defaultLifeTime);
    const [cookie, setCookie] = useState(getCookie);

    const updateCookie = useCallback((value: string, lifeTime?: number) => {
        if (lifeTime !== undefined) {
            lifeTimeRef.current = lifeTime;
        }
        initializedRef.current = true;
        setCookie(value);
    }, []);

    useEffect(() => {
        if (initializedRef.current) {
            setCookieValue(cookieName, cookie, lifeTimeRef.current);
            lifeTimeRef.current = defaultLifeTime;
        }
    }, [cookie, cookieName, lifeTimeRef, initializedRef, defaultLifeTime]);

    return [cookie, updateCookie];
};

export function useCookieJson<Type>(
    cookieName: string,
    defaultLifeTime = 0
): [Partial<Type>, (data: Partial<Type>, lifeTime?: number) => void] {
    const getCookieJson = () => getCookieJsonValue(cookieName);

    const initializedRef = useRef(false);
    const lifeTimeRef = useRef(defaultLifeTime);
    const [cookieJson, setCookieJson] = useState(getCookieJson);

    const updateCookieJson = useCallback(
        (data: Partial<Type>, lifeTime?: number) => {
            if (lifeTime !== undefined) {
                lifeTimeRef.current = lifeTime;
            }
            initializedRef.current = true;
            setCookieJson((currentCookieJson: Partial<Type>) => ({
                ...currentCookieJson,
                ...data,
            }));
        },
        []
    );

    useEffect(() => {
        if (initializedRef.current) {
            setCookieJsonValue(cookieName, cookieJson, lifeTimeRef.current);
            lifeTimeRef.current = defaultLifeTime;
        }
    }, [cookieJson, cookieName, lifeTimeRef, initializedRef, defaultLifeTime]);

    return [cookieJson, updateCookieJson];
}

export default useCookie;
